import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnInit,
  ViewChild
} from '@angular/core';
import { Form, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { from, take } from 'rxjs';
import { Toast } from 'src/app/interfaces/toast';
import { ToastService } from 'src/app/services/toast.service';

import { VendorPortalRedirectGuard } from '../../guards/vendor-portal-redirect.guard';
import { AuthService } from '../../services/auth.service';
import {
  SSO_SIGN_IN_ELSEWHERE_COPY,
  SSO_SIGN_IN_REQUIRED_COPY,
  isSsoSignInRequired
} from '../../services/vive-api-errors';
import { SsoLoginButtonComponent } from '../../shared-components/sso-login-button/sso-login-button.component';
import { authService as viveAuthService } from '../../shared/vive-api/auth.service';

@Component({
  selector: 'app-login-modal',
  templateUrl: './login-modal.component.html',
  styleUrls: ['./login-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class LoginModalComponent implements OnInit {
  loginModel = { email: '', password: '' };
  loginModalForm!: FormGroup;

  constructor(
    private authService: AuthService,
    private activeModal: NgbActiveModal,
    private router: Router,
    private ToastService: ToastService,
    private vendorPortalRedirectGuard: VendorPortalRedirectGuard,
    private cdr: ChangeDetectorRef
  ) {}

  @HostListener('keydown.enter', ['$event']) onKeydown(event: Event) {
    this.login();
  }

  isLoginError: boolean = false;
  // Shown in place of the no-match message when the account signs in with SSO.
  ssoRefusal: string | null = null;
  // The opener sets this false where SSO cannot finish the sign-in.
  public offerSso = true;
  @ViewChild('ssoLogin') private ssoLogin?: SsoLoginButtonComponent;

  forgotPassword() {
    this.close();
    this.router.navigate(['login', 'forgot-password']);
  }

  close() {
    this.activeModal.dismiss();
  }

  ngOnInit(): void {
    this.loginModalForm = new FormGroup({
      email: new FormControl(this.loginModel.email, [
        Validators.email,
        Validators.required
      ]),
      password: new FormControl(this.loginModel.password, [Validators.required])
    });
  }

  //Add validation to ensure the user entered an email & password.

  login(): void {
    this.ssoLogin?.discover();
    from(
      viveAuthService.createSession({
        data: {
          email: this.loginModalForm.value.email,
          password: this.loginModalForm.value.password
        }
      })
    ).subscribe({
      next: (session) => {
        this.authService.seedSession(session);
        this.isLoginError = false;
        this.ssoRefusal = null;
        // seedSession() reset the portal-routing cache, so the guard
        // re-fetches the cut-over decision for this user.
        // A vendor who became cut-over must be sent to vive-vendors on
        // re-auth, not dropped back into the legacy page they 401'd on. Only
        // close the modal when it is safe to stay; on the redirect branch the
        // guard has already initiated window.location.replace and the page is
        // unloading, so leave the modal in place.
        this.vendorPortalRedirectGuard
          .redirectToVendorPortalIfForced()
          .pipe(take(1))
          .subscribe((canStay) => {
            if (canStay) {
              this.activeModal.close(true);
            }
          });
      },
      error: (error: unknown) => {
        if (isSsoSignInRequired(error)) {
          this.isLoginError = false;
          this.ssoRefusal = this.offerSso
            ? SSO_SIGN_IN_REQUIRED_COPY
            : SSO_SIGN_IN_ELSEWHERE_COPY;
        } else {
          this.isLoginError = true;
          this.ssoRefusal = null;
        }
        // Zoneless: this async callback mutates state that gates the error
        // message, so nudge change detection or the "invalid credentials"
        // notice never renders.
        this.cdr.markForCheck();
      }
    });
  }
}
